import { Chip } from '@jotpsych/ui';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import { Box, Button, Divider, Skeleton, Stack, Typography } from '@mui/material';

import { formatIsoDate } from '../vm/formatters';
import type { DiagnosisVM } from '../vm/types';

const RESOLVED_CAP = 3;

interface DiagnosisPeekListProps {
  diagnoses: DiagnosisVM[];
  isLoading?: boolean;
  /** Opens the full edit modal (the peek itself is read-only). */
  onEdit?: () => void;
}

const statusChip = (dx: DiagnosisVM) =>
  dx.isSuggested ? (
    <Chip variant="soft" size="xSmall" color="info" label="Suggested" sx={{ flexShrink: 0 }} />
  ) : (
    <Chip
      variant="soft"
      size="xSmall"
      color={dx.isActive ? 'success' : 'default'}
      label={dx.isActive ? 'Active' : 'Resolved'}
      sx={{ flexShrink: 0 }}
    />
  );

/**
 * Compact hover-peek diagnosis list for the patient context bar, on the same
 * layout as MedicationPeekList: suggested rows first (pending review), active
 * uncapped, resolved capped with a count. Read-only; Edit opens the full modal
 * via the host.
 */
export const DiagnosisPeekList: React.FC<DiagnosisPeekListProps> = ({
  diagnoses,
  isLoading = false,
  onEdit,
}) => {
  const suggested = diagnoses.filter((dx) => dx.isSuggested);
  const accepted = diagnoses.filter((dx) => !dx.isSuggested);
  const active = accepted.filter((dx) => dx.isActive);
  const resolved = accepted.filter((dx) => !dx.isActive);
  const shownResolved = resolved.slice(0, RESOLVED_CAP);

  return (
    <Box sx={{ minWidth: 400, maxWidth: 600, p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <LocalHospitalOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          Diagnoses
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {diagnoses.length} total · {suggested.length + active.length} active
        </Typography>
        {onEdit && (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onEdit}
            sx={{ textTransform: 'none', minWidth: 'auto' }}
          >
            Edit
          </Button>
        )}
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {isLoading &&
          [0, 1, 2].map((row) => (
            <Skeleton key={row} variant="rounded" height={28} sx={{ my: 0.75 }} />
          ))}
        {!isLoading && diagnoses.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No diagnoses on file.
          </Typography>
        )}
        {[...suggested, ...active].map((dx) => (
          <Stack
            key={dx.id}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between', py: 0.75 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                {dx.name}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                {[dx.code, dx.dateAdded ? `added ${formatIsoDate(dx.dateAdded)}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
            {statusChip(dx)}
          </Stack>
        ))}

        {resolved.length > 0 && (
          <>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.disabled',
              }}
            >
              Resolved
            </Typography>
            {shownResolved.map((dx) => (
              <Stack
                key={dx.id}
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {dx.name}
                  {dx.endDate && (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {' '}
                      · resolved {formatIsoDate(dx.endDate)}
                    </Box>
                  )}
                </Typography>
                {statusChip(dx)}
              </Stack>
            ))}
            {resolved.length > RESOLVED_CAP && (
              <Typography variant="caption" color="text.disabled">
                …and {resolved.length - RESOLVED_CAP} more
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
